import * as Icons from "lucide-react";
import { PLANS, PRICING_DISCLAIMER } from "../lib/brand";
import { CTA, GlassCard, PageHeader, SectionHeading, Shell } from "../components/site/Primitives";

const VARIABLE_COSTS = [
  ["Telephony & voice minutes", "Billed by your voice provider based on call volume."],
  ["SMS & WhatsApp messages", "Billed per message by your messaging provider."],
  ["AI model usage", "Billed by model provider based on tokens or minutes used."],
  ["Custom integrations", "Scoped and quoted separately when required."],
];

const FAQ = [
  ["Is usage unlimited?", "No. Software and setup are fixed; communication and AI model usage are variable and stated separately so you can forecast them."],
  ["Do I need my own provider accounts?", "Yes. Voice, messaging, calendar and payment providers connect with your own credentials, which keeps ownership and billing with you."],
  ["Can I start with one module?", "Yes. Most clients start with the AI receptionist and CRM, then enable follow-up and reporting."],
  ["What happens after setup?", "You keep the configured system, the workflows and the data. Monthly work covers hosting, support and refinement."],
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHeader
        testId="pricing-title"
        eyebrow="Pricing"
        title="Clear plans. Clearly stated variable costs."
        sub="Choose the plan that matches how much of the customer journey you want automated."
      />

      <section className="py-20">
        <Shell>
          <div className="grid gap-4 lg:grid-cols-3">
            {PLANS.map((plan) => (
              <GlassCard
                key={plan.key}
                testId={`pricing-plan-${plan.key}`}
                className={`flex flex-col p-7 ${plan.highlight ? "border-elv-green/30 bg-elv-green/[0.04]" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-elv-dim">{plan.name}</p>
                  {plan.highlight ? (
                    <span className="rounded-sm border border-elv-green/30 bg-elv-green/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-elv-green">
                      Most chosen
                    </span>
                  ) : null}
                </div>
                <p className="mt-6 font-display text-5xl text-elv-text">
                  ${plan.price}
                  <span className="text-sm text-elv-dim">/month</span>
                </p>
                <p className="mt-2 text-xs text-elv-dim">${plan.setup} one-time setup</p>
                <p className="mt-5 text-sm leading-relaxed text-elv-dim">{plan.summary}</p>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-elv-dim">
                      <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <CTA
                    to={plan.ctaLink || "/book-demo"}
                    variant={plan.highlight ? "primary" : "ghost"}
                    testId={`pricing-cta-${plan.key}`}
                  >
                    {plan.cta || "Book a Demo"}
                  </CTA>
                </div>
              </GlassCard>
            ))}
          </div>

          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {VARIABLE_COSTS.map(([title, detail], i) => (
              <GlassCard key={title} testId={`variable-cost-${i}`} className="p-5">
                <Icons.Gauge className="h-4 w-4 text-elv-cyan" />
                <p className="mt-4 text-sm font-medium text-elv-text">{title}</p>
                <p className="mt-2 text-xs leading-relaxed text-elv-dim">{detail}</p>
              </GlassCard>
            ))}
          </div>

          <p data-testid="pricing-disclaimer" className="mt-10 max-w-3xl text-xs leading-relaxed text-elv-dim">
            {PRICING_DISCLAIMER}
          </p>
        </Shell>
      </section>

      <section className="border-t border-elv-line bg-[#070A0F] py-20">
        <Shell>
          <SectionHeading testId="pricing-faq-heading" eyebrow="Questions" title="What clients ask first." />
          <div className="mt-12 grid gap-3 md:grid-cols-2">
            {FAQ.map(([q, a], i) => (
              <GlassCard key={q} testId={`pricing-faq-${i}`} className="p-6">
                <p className="text-sm font-semibold text-elv-text">{q}</p>
                <p className="mt-3 text-sm leading-relaxed text-elv-dim">{a}</p>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>
    </div>
  );
}
